import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Form, Spinner, Alert, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { is_site_pre_processing, is_site_dashboard_pending, is_site_crawl_failed, is_competitor_site, get_pre_processing_label, compare_competitors } from '../components/SitesTable';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';
import { is_valid_site_url, c_InvalidSiteUrlText } from '../utils/SiteUrlValidation';

import './css/onboarding.css';

// How often the sites are reloaded while any of them is still being crawled or analyzed.
const c_ProgressPollIntervalMs = 3000;

const c_SitesPath = '/sites';

// A site whose work has finished; the grade follows once its analysis has one.
const c_DoneText = 'Done';

const c_CrawlFailedText = 'Could not crawl this site';

// A competitor waiting for its turn: competitors are processed only once the user's own site has finished.
const c_CompetitorQueuedText = 'Queued';

// The onboarding wizard, opened at login until the user leaves it: the user's own site and up to the most
// competitors a site can have, then the progress of each. It is marked as shown only when the user leaves it by
// Skip for now, Go to Sites or Open Dashboard, so a refresh brings it back, at the progress step once the sites are
// added. Each site is crawled on its own and a site that cannot
// be crawled shows why without holding up the others; the user's own site is crawled first, and its dashboard can be
// opened as soon as it is ready.
function OnboardingView(){
	const navigate = useNavigate();
	const { token } = useAuth();

	// False until the wizard is known to open and has loaded what it shows.
	const [ready, set_ready] = useState(false);

	const [max_competitor_sites, set_max_competitor_sites] = useState(0);
	const [max_competitor_site_pages, set_max_competitor_site_pages] = useState(0);

	const [main_name, set_main_name] = useState('');
	const [main_url, set_main_url] = useState('');
	const [main_description, set_main_description] = useState('');

	// The competitor entries, each { key, name, url }; key only tells the rows apart.
	const [competitors, set_competitors] = useState([]);
	const next_competitor_key_ref = useRef(1);

	const [saving, set_saving] = useState(false);
	const [error_message, set_error_message] = useState('');

	// Set once the sites are added: the ids of the user's site and its competitors, in that order, and the user's
	// sites as last loaded.
	const [added_site_ids, set_added_site_ids] = useState(null);
	const [sites, set_sites] = useState([]);

	const mounted_ref = useRef(true);
	const poll_timeout_ref = useRef(null);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	const stop_polling = ()=>{
		if(poll_timeout_ref.current){
			clearTimeout(poll_timeout_ref.current);
			poll_timeout_ref.current = null;
		}
	};

	useEffect(()=>{
		mounted_ref.current = true;
		open_wizard();

		return ()=>{
			mounted_ref.current = false;
			stop_polling();
		};
	}, []);

	// The wizard opens only until the user has left it by one of its buttons (see finish_wizard); otherwise the user
	// goes to the site list. A user who already has a site, as after a refresh of the progress step, sees the
	// progress of that site and its competitors rather than the form.
	const open_wizard = async()=>{
		try {
			const status_response = await axios.get(Constants.SERVER_URL + '/api/onboarding_status', { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			if(!status_response.data || !status_response.data.show_onboarding){
				navigate(c_SitesPath, { replace: true });
				return;
			}

			const limits_response = await axios.get(Constants.SERVER_URL + '/api/site_limits', { headers: get_headers() });
			const sites_response = await axios.get(Constants.SERVER_URL + '/api/sites/', { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			set_max_competitor_sites(limits_response.data.max_competitor_sites);
			set_max_competitor_site_pages(limits_response.data.max_competitor_site_pages);

			const loaded_sites = (sites_response.data && sites_response.data.sites) ? sites_response.data.sites : [];
			const existing_site_ids = get_existing_site_ids(loaded_sites);
			if(existing_site_ids){
				set_added_site_ids(existing_site_ids);
				set_sites(loaded_sites);
				schedule_reload(loaded_sites);
			}

			set_ready(true);
		}
		catch (error) {
			console.log('OnboardingView/open_wizard - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				navigate(c_SitesPath, { replace: true });
			}
		}
	};

	const add_competitor_entry = ()=>{
		if(competitors.length >= max_competitor_sites){
			return;
		}

		const key = next_competitor_key_ref.current;
		next_competitor_key_ref.current = key + 1;
		set_competitors(competitors.concat([{ key, name: '', url: '' }]));
	};

	const remove_competitor_entry = (key)=>{
		set_competitors(competitors.filter((competitor)=>{ return competitor.key !== key; }));
	};

	const update_competitor_entry = (key, field, value)=>{
		set_competitors(competitors.map((competitor)=>{
			if(competitor.key !== key){
				return competitor;
			}

			const updated = Object.assign({}, competitor);
			updated[field] = value;
			return updated;
		}));
	};

	// The first problem with what was entered, or '' when it can be sent.
	const get_form_error = ()=>{
		if((main_name.trim().length === 0) || (main_url.trim().length === 0)){
			return 'Enter your site\'s name and url.';
		}

		if(!is_valid_site_url(main_url)){
			return c_InvalidSiteUrlText;
		}

		const incomplete = competitors.some((competitor)=>{
			return (competitor.name.trim().length === 0) || (competitor.url.trim().length === 0);
		});
		if(incomplete){
			return 'Enter a name and url for each competitor, or remove it.';
		}

		const invalid_competitor = competitors.find((competitor)=>{ return !is_valid_site_url(competitor.url); });
		if(invalid_competitor){
			return c_InvalidSiteUrlText + ' Check ' + invalid_competitor.url.trim() + '.';
		}

		return '';
	};

	const start = async(event)=>{
		event.preventDefault();

		const form_error = get_form_error();
		if(form_error){
			set_error_message(form_error);
			return;
		}

		set_error_message('');
		set_saving(true);

		try {
			const payload = {
				main: { name: main_name.trim(), url: main_url.trim(), description: main_description.trim() },
				competitors: competitors.map((competitor)=>{
					return { name: competitor.name.trim(), url: competitor.url.trim() };
				})
			};

			const response = await axios.post(Constants.SERVER_URL + '/api/onboarding_sites', payload, { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			const site_ids = [response.data.site.id].concat(response.data.competitors.map((competitor)=>{ return competitor.id; }));
			set_added_site_ids(site_ids);
			set_saving(false);
			await load_sites();
		}
		catch (error) {
			console.log('OnboardingView/start - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_error_message(get_user_error_message(error, 'Your sites could not be added. Please try again.'));
				set_saving(false);
			}
		}
	};

	// Reloads the sites again later while any of them is still being crawled or analyzed.
	const schedule_reload = (loaded_sites)=>{
		stop_polling();

		const pre_processing = loaded_sites.some((site)=>{ return is_site_pre_processing(site); });
		if(!pre_processing || !mounted_ref.current){
			return;
		}

		poll_timeout_ref.current = setTimeout(()=>{
			poll_timeout_ref.current = null;
			load_sites();
		}, c_ProgressPollIntervalMs);
	};

	const load_sites = async()=>{
		try {
			const response = await axios.get(Constants.SERVER_URL + '/api/sites/', { headers: get_headers() });
			const loaded_sites = (response.data && response.data.sites) ? response.data.sites : [];
			if(!mounted_ref.current){
				return;
			}

			set_error_message('');
			set_sites(loaded_sites);
			schedule_reload(loaded_sites);
		}
		catch (error) {
			console.log('OnboardingView/load_sites - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_error_message(get_user_error_message(error, 'The progress of your sites could not be loaded.'));
				schedule_retry();
			}
		}
	};

	// After a failed load the sites are loaded again later, so the progress recovers once the server answers.
	const schedule_retry = ()=>{
		stop_polling();
		poll_timeout_ref.current = setTimeout(()=>{
			poll_timeout_ref.current = null;
			load_sites();
		}, c_ProgressPollIntervalMs);
	};

	const get_site = (site_id)=>{
		const found = sites.find((site)=>{ return site.id === site_id; });
		return found ? found : null;
	};

	// Leaves the wizard by one of its buttons, which is what marks it as shown, so it does not open again. A refresh
	// or a closed tab leaves it unmarked, and it opens again. A failure to mark it is logged and the user still goes on.
	const finish_wizard = async(path)=>{
		stop_polling();

		try {
			await axios.put(Constants.SERVER_URL + '/api/onboarding_shown', {}, { headers: get_headers() });
		}
		catch (error) {
			console.log('OnboardingView/finish_wizard - ERROR: ', describe_error(error));
		}

		navigate(path);
	};

	const go_to_sites = ()=>{
		finish_wizard(c_SitesPath);
	};

	const open_dashboard = ()=>{
		finish_wizard('/site/' + added_site_ids[0] + '/dashboard');
	};

	// The ids the progress step shows for sites already added, as after a refresh of the wizard: the user's newest
	// own site, then its competitors in competitor order. Null when the user has no site of their own yet.
	const get_existing_site_ids = (loaded_sites)=>{
		const main_sites = loaded_sites.filter((site)=>{ return !is_competitor_site(site); });
		if(main_sites.length === 0){
			return null;
		}

		main_sites.sort((first, second)=>{
			return new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime();
		});

		const main_site = main_sites[0];
		const competitors_of_main = loaded_sites.filter((site)=>{ return site.competitor_of_site_id === main_site.id; });
		competitors_of_main.sort(compare_competitors);

		return [main_site.id].concat(competitors_of_main.map((site)=>{ return site.id; }));
	};

	// A url as shown: no scheme, no leading www., no trailing slash.
	const strip_url = (url)=>{
		return String(url).replace('https://', '').replace('http://', '').replace(/^www\./i, '').replace(/\/+$/, '');
	};

	const render_loading = ()=>{
		return (
			<Container className="view-container onboarding-view">
				<div className="onboarding-loading">
					<Spinner animation="border" size="sm"/>
				</div>
			</Container>
		);
	};

	const render_main_site_fields = ()=>{
		return (
			<Card className="onboarding-card">
				<h5 className="onboarding-section-title">Your site</h5>
				<Form.Group className="onboarding-field">
					<Form.Label>Site name</Form.Label>
					<Form.Control
						type="text"
						value={main_name}
						placeholder="Site name"
						disabled={saving}
						onChange={(event)=>{ set_main_name(event.target.value); }}/>
				</Form.Group>
				<Form.Group className="onboarding-field">
					<Form.Label>Url</Form.Label>
					<Form.Control
						type="text"
						value={main_url}
						placeholder="https://example.com"
						disabled={saving}
						onChange={(event)=>{ set_main_url(event.target.value); }}/>
				</Form.Group>
				<Form.Group className="onboarding-field">
					<Form.Label>Description (optional)</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						value={main_description}
						placeholder="What the site is about"
						disabled={saving}
						onChange={(event)=>{ set_main_description(event.target.value); }}/>
				</Form.Group>
			</Card>
		);
	};

	const render_competitor_entry = (competitor)=>{
		return (
			<Row key={competitor.key} className="onboarding-competitor-row">
				<Col className="onboarding-competitor-col">
					<Form.Control
						type="text"
						value={competitor.name}
						placeholder="Name"
						disabled={saving}
						onChange={(event)=>{ update_competitor_entry(competitor.key, 'name', event.target.value); }}/>
				</Col>
				<Col className="onboarding-competitor-col">
					<Form.Control
						type="text"
						value={competitor.url}
						placeholder="https://competitor.com"
						disabled={saving}
						onChange={(event)=>{ update_competitor_entry(competitor.key, 'url', event.target.value); }}/>
				</Col>
				<Col className="onboarding-competitor-remove-col">
					<Button
						variant="outline-danger"
						size="sm"
						title="Remove this competitor"
						disabled={saving}
						onClick={()=>{ remove_competitor_entry(competitor.key); }}>
						✕
					</Button>
				</Col>
			</Row>
		);
	};

	const render_add_competitor_button = ()=>{
		if(competitors.length >= max_competitor_sites){
			return null;
		}

		return (
			<Button
				variant="outline-primary"
				className="onboarding-add-competitor-button"
				title="Add a competitor"
				disabled={saving}
				onClick={add_competitor_entry}>
				+ Add a competitor
			</Button>
		);
	};

	const render_competitor_fields = ()=>{
		return (
			<Card className="onboarding-card">
				<h5 className="onboarding-section-title">{'Competitors (optional, up to ' + max_competitor_sites + ')'}</h5>
				<p className="onboarding-text">
					Your site is compared with each competitor on its dashboard and in its PDF report.
				</p>
				<p className="onboarding-credits-warning">
					{'Each competitor is crawled and analyzed like your own site and uses your analysis credits: up to ' +
						max_competitor_site_pages + ' credits per site.'}
				</p>
				{competitors.map((competitor)=>{ return render_competitor_entry(competitor); })}
				{render_add_competitor_button()}
			</Card>
		);
	};

	const render_form_actions = ()=>{
		return (
			<div className="onboarding-actions">
				<Button variant="secondary" type="button" disabled={saving} onClick={go_to_sites}>
					Skip for now
				</Button>
				<Button variant="primary" type="submit" className="onboarding-start-button" disabled={saving}>
					{saving ? <Spinner animation="border" size="sm"/> : 'Start'}
				</Button>
			</div>
		);
	};

	const render_form = ()=>{
		return (
			<Container className="view-container onboarding-view">
				<h1 className="onboarding-title">Welcome to AEO Audited</h1>
				<p className="onboarding-text">
					Add your site to see how ready it is for AI answer engines. Your site is crawled and analyzed first.
				</p>
				{error_message && <Alert variant="danger">{error_message}</Alert>}
				<Form onSubmit={start}>
					{render_main_site_fields()}
					{render_competitor_fields()}
					{render_form_actions()}
				</Form>
			</Container>
		);
	};

	// A site's stage on the progress step: while it is worked on, what it is doing with its progress, as the site
	// list shows it (get_pre_processing_label); once finished, Done with its grade.
	const get_progress_text = (site)=>{
		if(is_site_crawl_failed(site)){
			return c_CrawlFailedText;
		}

		if(is_competitor_site(site) && (site.crawl_stage === 'queued')){
			return c_CompetitorQueuedText;
		}

		// Every stage of the work in progress is shown as the site list shows it, with its progress.
		if(is_site_pre_processing(site)){
			return get_pre_processing_label(site);
		}

		if(site.latest_analysis && site.latest_analysis.grade){
			return c_DoneText + ', grade ' + site.latest_analysis.grade;
		}

		return c_DoneText;
	};

	const render_progress_status = (site)=>{
		if(is_site_crawl_failed(site)){
			return (
				<div className="onboarding-progress-status onboarding-progress-failed">
					{c_CrawlFailedText}
					{site.crawl_error && <div className="onboarding-progress-error">{site.crawl_error}</div>}
				</div>
			);
		}

		// A competitor waiting its turn is not being worked on, so it has no spinner.
		const is_waiting = is_competitor_site(site) && (site.crawl_stage === 'queued');

		return (
			<div className={'onboarding-progress-status' + (is_waiting ? ' onboarding-progress-queued' : '')}>
				{(is_site_pre_processing(site) && !is_waiting) && <Spinner animation="border" size="sm" className="onboarding-progress-spinner"/>}
				{get_progress_text(site)}
			</div>
		);
	};

	const render_progress_row = (site_id, site_index)=>{
		const site = get_site(site_id);
		if(!site){
			return null;
		}

		return (
			<Row key={site_id} className="onboarding-progress-row">
				<Col className="onboarding-progress-site-col">
					<div className="onboarding-progress-name">
						{site.title}
						{(site_index > 0) && <span className="sites-row-competitor-tag onboarding-progress-tag">Competitor</span>}
					</div>
					<div className="onboarding-progress-url">{strip_url(site.url)}</div>
				</Col>
				<Col className="onboarding-progress-status-col">
					{render_progress_status(site)}
				</Col>
			</Row>
		);
	};

	// The user's own site's dashboard opens once its crawl and history are in, whatever its competitors are doing.
	const is_dashboard_ready = ()=>{
		const main_site = get_site(added_site_ids[0]);
		if(!main_site){
			return false;
		}

		return !is_site_dashboard_pending(main_site) && !is_site_crawl_failed(main_site);
	};

	// The added sites in display order: the user's own site, then its competitors in competitor order once loaded.
	const get_display_site_ids = ()=>{
		const competitor_ids = added_site_ids.slice(1);
		competitor_ids.sort((first_id, second_id)=>{
			const first_site = get_site(first_id);
			const second_site = get_site(second_id);
			if(!first_site || !second_site){
				return 0;
			}

			return compare_competitors(first_site, second_site);
		});

		return [added_site_ids[0]].concat(competitor_ids);
	};

	const render_progress = ()=>{
		return (
			<Container className="view-container onboarding-view">
				<h1 className="onboarding-title">Analyzing your sites</h1>
				<p className="onboarding-text">
					Your site is crawled and analyzed first, then each competitor. This can take a while. Your site's
					dashboard opens once its pages have been crawled and analyzed; its history is added after. You can
					leave this page: the work carries on and your sites are listed under Sites.
				</p>
				{error_message && <Alert variant="danger">{error_message}</Alert>}
				<Card className="onboarding-card">
					{get_display_site_ids().map((site_id, site_index)=>{ return render_progress_row(site_id, site_index); })}
				</Card>
				<div className="onboarding-actions">
					<Button variant="secondary" onClick={go_to_sites}>
						Go to Sites
					</Button>
					<Button variant="primary" className="onboarding-start-button" disabled={!is_dashboard_ready()} onClick={open_dashboard}>
						Open Dashboard
					</Button>
				</div>
			</Container>
		);
	};

	if(!ready){
		return render_loading();
	}

	if(added_site_ids){
		return render_progress();
	}

	return render_form();
}

export default OnboardingView;
